import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";

import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import AttachResourceFormDialog from "../Resource/AttachResourceFormDialog";
import AttachDevelopmentNeedFormDialog from "../DevelopmentNeed/AttachDevelopmentNeedFormDialog";
import ActivityForm from "./ActivityForm";
import ActivityReviewFormDialog from "./ActivityReviewFormDialog";
import ActivityReviewStatusIcon from "./ActivityReviewStatusIcon";
import DisplayCategories from "../Category/DisplayCategories";

import { addActivity, deleteActivity, updateActivity, getAppraisalDetails } from "../../../../actions/rmsActions";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: 12,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 18,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const LinkBuilder = ({ text, id, enabled = false }) => {
    const location = useLocation();
    const refererPath = encodeURIComponent(location.pathname);

    const label = text?.length > 0 ? text : "No title";

    let display = label;

    if (enabled && id) {
        display = (
            <Link to={`/portfolio/activities/${Number(id)}/?rp=${refererPath}`}>
                {label}
            </Link>
        );
    }

    return display;
};


function ActivityTable({items, canUpdate, appraisalId = null, inputSegmentId = null, appCode}) {
    const [openDialog, setOpenDialog] = useState(null);
    const [selected, setSelected] = useState(null);

    const dispatch = useDispatch();
    const handleAdd = (params) => dispatch(addActivity(appCode, params, appraisalId, inputSegmentId, () => dispatch(getAppraisalDetails(appraisalId, appCode))));
    const handleEdit = (id, params) => dispatch(updateActivity(appCode, id, params, appraisalId));
    const handleDelete = (id) => dispatch(deleteActivity(appCode, id, appraisalId));

    return (
        <Box className="card-content-full-width">
            <TableContainer>
                <Table sx={{ minWidth: 500 }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Activity</StyledTableCell>
                            <StyledTableCell align="left">Type&nbsp;</StyledTableCell>
                            <StyledTableCell>Categories</StyledTableCell>
                            <StyledTableCell>&nbsp;</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items
                            .sort((a, b) => a.id - b.id)
                            .map((row) => (
                                <StyledTableRow key={row.id}>
                                    <StyledTableCell>
                                        <LinkBuilder text={row?.title} id={row?.id} enabled={true} />
                                    </StyledTableCell>
                                    <StyledTableCell align="left">{row?.type?.name ?? "Undefined"}</StyledTableCell>
                                    <StyledTableCell>
                                        <DisplayCategories categories={row?.categories} />
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Stack direction="row" justifyContent="flex-end" spacing={0} >
                                            {!canUpdate && row?.developmentNeeds?.length > 0 && (
                                                <Tooltip title="Development needs" placement="top" arrow >
                                                    <IconButton
                                                        aria-label="development-needs"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("developmentNeed");
                                                        }}
                                                    >
                                                        <AutoGraphIcon fontSize="small" color="success" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {!canUpdate &&
                                                row?.resources?.length > 0 && (
                                                    <Tooltip title="Attached resources" placement="top" arrow >
                                                        <IconButton
                                                            aria-label="attachments"
                                                            onClick={() => {
                                                                setSelected(row);
                                                                setOpenDialog("resource");
                                                            }}
                                                        >
                                                            <AttachFileIcon fontSize="small" color="success" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                            <Tooltip title="Reflection" placement="top" arrow >
                                                <IconButton
                                                    aria-label="reflection"
                                                    onClick={() => {
                                                        setSelected(row);
                                                        setOpenDialog("review");
                                                    }}
                                                >
                                                    <ActivityReviewStatusIcon
                                                        status={row?.review?.status}
                                                        isRequired={row?.review?.isRequired}
                                                    />
                                                </IconButton>
                                            </Tooltip>

                                            {canUpdate && (
                                                <>
                                                    <Tooltip title="Development needs" placement="top" arrow >
                                                        <IconButton
                                                            aria-label="development-needs"
                                                            onClick={() => {
                                                                setSelected(row);
                                                                setOpenDialog("developmentNeed");
                                                            }}
                                                        >
                                                            <AutoGraphIcon
                                                                fontSize="small"
                                                                color={row?.developmentNeeds?.length > 0 ? "success" : "default"}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Attached resources" placement="top" arrow >
                                                        <IconButton
                                                            aria-label="attachments"
                                                            onClick={() => {
                                                                setSelected(row);
                                                                setOpenDialog("resource");
                                                            }}
                                                        >
                                                            <AttachFileIcon
                                                                fontSize="small"
                                                                color={row?.resources?.length > 0 ? "success" : "default"}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <IconButton
                                                        aria-label="edit"
                                                        disabled={!row.canUpdate}
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("edit");
                                                        }}
                                                    >
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="delete"
                                                        disabled={!row.canDelete}
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("delete");
                                                        }}
                                                    >
                                                        <DeleteOutlinedIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        <StyledTableRow key="totals">
                            <StyledTableCell sx={{ backgroundColor: "#FFFFFF" }} colSpan={4}/>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {canUpdate && (
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} >
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        color="secondary"
                        aria-label="add activity"
                        disableElevation={true}
                        onClick={() => setOpenDialog("add")}
                    >
                        Add
                    </Button>
                </Stack>
            )}

            {(openDialog === "add" || (openDialog === "edit" && !!selected)) && (
                <ActivityForm
                    open={true}
                    formTitle={`${openDialog === "add" ? "Add" : "Edit"} activity`}
                    data={openDialog === "edit" && selected}
                    onSave={openDialog === "add" ? handleAdd : (params) => handleEdit(selected.id, params)}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                    scroll="paper"
                />
            )}

            {openDialog === "review" && !!selected && (
                <ActivityReviewFormDialog
                    open={true}
                    formTitle="Reflection"
                    data={selected}
                    canUpdate={canUpdate && selected?.canUpdate}
                    onSave={(params) => handleEdit(selected.id, { ...selected, review: params })}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    scroll="paper"
                />
            )}

            {openDialog === "developmentNeed" && !!selected && (
                <AttachDevelopmentNeedFormDialog
                    open={true}
                    data={selected}
                    onSave={(tempAttached) => handleEdit(selected.id, {...selected, developmentNeeds: tempAttached})}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    canUpdate={canUpdate && selected?.canUpdate}
                    appCode={appCode}
                />
            )}

            {openDialog === "resource" && !!selected && (
                <AttachResourceFormDialog
                    open={true}
                    data={selected}
                    onSave={(tempAttached) => handleEdit(selected.id, {...selected, resources: tempAttached})}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    canUpdate={canUpdate && selected?.canUpdate}
                    appCode={appCode}
                />
            )}

            {openDialog === "delete" && !!selected && (
                <ConfirmDialog
                    open={true}
                    title="Delete activity"
                    content={`The following activity will be deleted: ${selected.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => handleDelete(selected.id)}
                    onCancel={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                />
            )}
        </Box>
    );
}

export default ActivityTable;
